import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import MediaModal from "@/components/home/MediaModal";

const HERO_IMG = "/deck-cover.jpg";
const HERO_TRACK = {
  kind: "audio",
  title: "Zo Pound World Wide 3",
  description: "The official Zo Pound anthem. Press play and step into the movement.",
  storage_path: "zo-pound-experience/media/c4b5527a-553c-4039-b3a2-45c661d675ab/572bf738-4110-4dfa-860d-5b38498ff436.wav",
};

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const contentFade = useTransform(scrollY, [0, 550], [1, 0.15]);
  const [playing, setPlaying] = useState(false);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 bg-ink">
        <img
          src={HERO_IMG}
          alt="The Zo Pound Experience — EPK deck cover art starring Ali Zo"
          data-testid="hero-background-image"
          className="w-full h-full object-cover lg:object-contain object-center opacity-90"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/25 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" />
      <div className="absolute inset-0 spotlight" />

      {/* 35mm frame corners */}
      <div className="absolute top-20 left-6 w-10 h-10 border-t-2 border-l-2 border-blood/70" />
      <div className="absolute top-20 right-6 w-10 h-10 border-t-2 border-r-2 border-blood/70" />
      <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-royal/70" />
      <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-royal/70" />

      <motion.div style={{ opacity: contentFade }} className="relative z-10 px-4 sm:px-8 lg:px-16 w-full max-w-7xl lg:max-w-[38vw] mx-auto lg:mx-0 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
          data-testid="hero-flag-badge"
        >
          <span className="flex flex-col w-8 h-5 overflow-hidden">
            <span className="flex-1 bg-royal" />
            <span className="flex-1 bg-blood" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gray-400">
            Haiti × Miami — Est. Late 90s
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight" data-testid="hero-headline">
          <MaskedLine delay={0.35} className="text-4xl sm:text-5xl lg:text-6xl text-gray-300">
            The
          </MaskedLine>
          <MaskedLine delay={0.48} className="text-7xl sm:text-8xl lg:text-[7rem]">
            Zo <span className="text-blood">Pound</span>
          </MaskedLine>
          <MaskedLine delay={0.61} className="text-7xl sm:text-8xl lg:text-[7rem]">
            <span className="text-outline-red">Experience</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-gray-200 font-bold leading-relaxed"
          data-testid="hero-subcopy"
        >
          Powerful information about what's going on around the world — including the US —
          from a Haitian/American point of view. Introducing <span className="text-white">Ali Zo</span>,
          founder of the Zo Pound movement. They deserve respect.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-listen-now-button"
            onClick={() => setPlaying(true)}
            className="group flex items-center gap-3 bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 hover:gap-4"
          >
            <Play size={18} fill="currentColor" /> Listen Now
          </button>
          <button
            data-testid="hero-manifesto-button"
            onClick={() => scrollToId("manifesto")}
            className="font-display font-bold uppercase tracking-wider text-lg px-8 py-4 border border-white/25 hover:border-blood hover:text-blood transition-colors duration-300"
          >
            Read the Manifesto
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-14 flex items-center justify-between font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-500"
        >
          <span data-testid="hero-production-credit">A Simplicity Films, LLC Production</span>
          <button
            data-testid="hero-scroll-down-button"
            onClick={() => scrollToId("manifesto")}
            className="flex items-center gap-2 hover:text-blood transition-colors"
            aria-label="Scroll down"
          >
            Scroll <ChevronDown size={14} className="animate-bounce" />
          </button>
        </motion.div>
      </motion.div>
      <MediaModal item={playing ? HERO_TRACK : null} onClose={() => setPlaying(false)} />
    </section>
  );
}
